import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  BuildingLibraryIcon,
  CubeIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ShieldExclamationIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  UserPlusIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { StatsCard, StatsGrid, ActivityCard, Card, CardHeader } from '@/components/common';
import { useAdminStats, useSystemHealth, useRecentActivity } from '@/hooks/useAdminDashboard';
import { useAuthStore } from '@/store/auth';
import { cn, formatCurrency as formatInr } from '@/utils';
import type { ActivityItem } from '@/components/common';
import api from '@/services/api';

function formatCurrency(value: number): string {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  }
  return formatInr(value);
}

// Platform stats for SuperAdmin view
interface PlatformStats {
  total_country_admins: number;
  active_countries: number;
  total_brokers: number;
  total_companies: number;
}

const fetchPlatformStats = async (): Promise<PlatformStats | null> => {
  try {
    const response = await api.get('/api/v1/users/platform-stats/');
    return response.data;
  } catch {
    return null;
  }
};

// SuperAdmin quick actions — CA-centric
const superAdminQuickActions = [
  {
    name: 'Manage Country Admins',
    description: 'View and manage country admins',
    href: '/admin/country-admins',
    icon: UsersIcon,
    color: 'bg-blue-500',
  },
  {
    name: 'Manage Countries',
    description: 'Configure countries on the platform',
    href: '/admin/geography/countries',
    icon: GlobeAltIcon,
    color: 'bg-green-500',
  },
  {
    name: 'Platform Settings',
    description: 'System-wide configuration',
    href: '/settings',
    icon: Cog6ToothIcon,
    color: 'bg-gray-500',
  },
];

// CountryAdmin quick actions — kept as before
const countryAdminQuickActions = [
  {
    name: 'Add Insurer',
    description: 'Register a new insurance company',
    href: '/admin/insurers/new',
    icon: BuildingLibraryIcon,
    color: 'bg-blue-500',
  },
  // These two used to point at /admin/wizards/product-setup and
  // /admin/wizards/user-setup. Both wizards are mockups that call no API, so
  // their routes were withdrawn -- which left these tiles landing on the
  // 404 page. Pointed at the real screens that do the same job instead.
  // (Onboarding is a working wizard again as of #209 and keeps its tile.)
  {
    name: 'Create Product',
    description: 'Setup a new insurance product',
    href: '/admin/products',
    icon: CubeIcon,
    color: 'bg-green-500',
  },
  {
    name: 'Add User',
    description: 'Create a new system user',
    href: '/admin/users',
    icon: UserPlusIcon,
    color: 'bg-purple-500',
  },
  {
    name: 'Onboarding Wizard',
    description: 'Setup country, insurer & broker',
    href: '/admin/wizards/onboarding',
    icon: RocketLaunchIcon,
    color: 'bg-orange-500',
  },
  {
    name: 'View Reports',
    description: 'Analytics and insights',
    href: '/reports',
    icon: ChartBarIcon,
    color: 'bg-indigo-500',
  },
  {
    name: 'Settings',
    description: 'System configuration',
    href: '/settings',
    icon: Cog6ToothIcon,
    color: 'bg-gray-500',
  },
];

function SystemHealthCard() {
  const { data: health, isLoading } = useSystemHealth();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'down':
      case 'disconnected':
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return 'text-green-600 dark:text-green-400';
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'down':
      case 'disconnected':
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader title="System Health" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="System Health" />
      <div className="space-y-4">
        {/* Core Services */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            {getStatusIcon(health?.database || 'healthy')}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Database</p>
              <p className={cn('text-xs capitalize', getStatusClass(health?.database || 'healthy'))}>
                {health?.database || 'Healthy'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            {getStatusIcon(health?.api || 'healthy')}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">API</p>
              <p className={cn('text-xs capitalize', getStatusClass(health?.api || 'healthy'))}>
                {health?.api || 'Healthy'}
              </p>
            </div>
          </div>
        </div>

        {/* Integrations */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
            Integrations
          </p>
          <div className="space-y-2">
            {health?.integrations?.slice(0, 4).map((integration) => (
              <div
                key={integration.id}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  {getStatusIcon(integration.status)}
                  <span className="text-sm text-gray-900 dark:text-white">{integration.name}</span>
                </div>
                <span className={cn('text-xs capitalize', getStatusClass(integration.status))}>
                  {integration.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

function QuickActionsGrid({ actions }: { actions: typeof superAdminQuickActions }) {
  return (
    <Card>
      <CardHeader title="Quick Actions" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {actions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-center group"
          >
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center mb-2',
                action.color
              )}
            >
              <action.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">
              {action.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{action.description}</p>
          </Link>
        ))}
      </div>
    </Card>
  );
}

export function SuperAdminDashboard() {
  const { user, countryContext } = useAuthStore();
  const { data: stats, isLoading: statsLoading } = useAdminStats();
  const { data: activities, isLoading: activitiesLoading } = useRecentActivity(5);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [platformStatsLoading, setPlatformStatsLoading] = useState(false);

  const isCountryAdmin = user?.role === 'country_admin';
  const dashboardTitle = isCountryAdmin
    ? `${countryContext?.name ?? 'Country'} Admin Dashboard`
    : 'Super Admin Dashboard';
  const dashboardSubtitle = isCountryAdmin
    ? `Managing ${countryContext?.name ?? 'country'} operations.`
    : 'Platform overview — manage country admins and global configuration.';

  useEffect(() => {
    if (!isCountryAdmin) {
      setPlatformStatsLoading(true);
      fetchPlatformStats().then((data) => {
        setPlatformStats(data);
        setPlatformStatsLoading(false);
      });
    }
  }, [isCountryAdmin]);

  // Map API activity to ActivityItem format
  const mappedActivities: ActivityItem[] = (activities || []).map((activity) => ({
    id: activity.id,
    type: activity.type,
    title: activity.title,
    description: activity.description,
    timestamp: activity.timestamp,
    user: activity.user
      ? {
          name: activity.user.name,
          avatar: activity.user.avatar,
        }
      : undefined,
  }));

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{dashboardTitle}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {dashboardSubtitle}
        </p>
      </div>

      {isCountryAdmin ? (
        /* ── CountryAdmin view ── */
        <>
          {/* Primary Stats */}
          <StatsGrid columns={4}>
            <StatsCard
              title="Total Users"
              value={stats?.totalUsers || 0}
              icon={UsersIcon}
              trend={stats?.trends?.users ? { value: stats.trends.users, isPositive: true, label: 'vs last month' } : undefined}
              loading={statsLoading}
              href="/admin/users"
            />
            <StatsCard
              title="Insurers"
              value={stats?.totalInsurers || 0}
              icon={BuildingLibraryIcon}
              trend={stats?.trends?.insurers ? { value: stats.trends.insurers, isPositive: true, label: 'vs last month' } : undefined}
              loading={statsLoading}
              href="/admin/insurers"
            />
            <StatsCard
              title="Products"
              value={stats?.totalProducts || 0}
              icon={CubeIcon}
              trend={stats?.trends?.products ? { value: stats.trends.products, isPositive: true, label: 'vs last month' } : undefined}
              loading={statsLoading}
              href="/admin/products"
            />
            <StatsCard
              title="Total Policies"
              value={stats?.totalPolicies || 0}
              icon={DocumentTextIcon}
              trend={stats?.trends?.policies ? { value: stats.trends.policies, isPositive: true, label: 'vs last month' } : undefined}
              loading={statsLoading}
              href="/policies"
            />
          </StatsGrid>

          {/* Secondary Stats */}
          <StatsGrid columns={4}>
            <StatsCard
              title="Total Premium"
              value={formatCurrency(stats?.totalPremium || 0)}
              icon={DocumentTextIcon}
              loading={statsLoading}
              iconClassName="bg-green-50 dark:bg-green-900/20"
            />
            <StatsCard
              title="Active Quotes"
              value={stats?.activeQuotes || 0}
              icon={ClipboardDocumentListIcon}
              loading={statsLoading}
              iconClassName="bg-blue-50 dark:bg-blue-900/20"
              href="/quotes"
            />
            <StatsCard
              title="Pending Claims"
              value={stats?.pendingClaims || 0}
              icon={ShieldExclamationIcon}
              loading={statsLoading}
              iconClassName="bg-orange-50 dark:bg-orange-900/20"
              href="/claims"
            />
            <StatsCard
              title="Upcoming Renewals"
              value={stats?.upcomingRenewals || 0}
              icon={ArrowPathIcon}
              loading={statsLoading}
              iconClassName="bg-purple-50 dark:bg-purple-900/20"
              href="/renewals"
            />
          </StatsGrid>

          {/* Quick Actions & Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <QuickActionsGrid actions={countryAdminQuickActions} />
            </div>
            <div>
              <ActivityCard
                activities={mappedActivities}
                loading={activitiesLoading}
                maxItems={5}
                viewAllHref="/admin/audit-logs"
              />
            </div>
          </div>

          {/* System Health + Admin Shortcuts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SystemHealthCard />

            <Card>
              <CardHeader title="Admin Shortcuts" />
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: 'Countries', href: '/admin/geography/countries', count: '5' },
                  { name: 'States', href: '/admin/geography/states', count: '36' },
                  { name: 'Vehicle Makes', href: '/admin/vehicles/makes', count: '25' },
                  { name: 'Vehicle Models', href: '/admin/vehicles/models', count: '180' },
                  { name: 'Insurance Types', href: '/admin/products/types', count: '4' },
                  { name: 'Account Types', href: '/admin/ledger/account-types', count: '12' },
                ].map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.count}</span>
                  </Link>
                ))}
              </div>
            </Card>
          </div>
        </>
      ) : (
        /* ── SuperAdmin view — CA-centric ── */
        <>
          {/* Platform Stats */}
          <StatsGrid columns={4}>
            <StatsCard
              title="Country Admins"
              value={platformStats?.total_country_admins ?? 0}
              icon={UsersIcon}
              loading={platformStatsLoading}
              href="/admin/country-admins"
            />
            <StatsCard
              title="Active Countries"
              value={platformStats?.active_countries ?? 0}
              icon={GlobeAltIcon}
              loading={platformStatsLoading}
              href="/admin/geography/countries"
            />
            <StatsCard
              title="Total Brokers"
              value={platformStats?.total_brokers ?? 0}
              icon={BuildingOffice2Icon}
              loading={platformStatsLoading}
            />
            <StatsCard
              title="Total Companies"
              value={platformStats?.total_companies ?? 0}
              icon={BuildingLibraryIcon}
              loading={platformStatsLoading}
            />
          </StatsGrid>

          {/* Quick Actions & Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <QuickActionsGrid actions={superAdminQuickActions} />
            </div>
            <div>
              <ActivityCard
                activities={mappedActivities}
                loading={activitiesLoading}
                maxItems={5}
                viewAllHref="/admin/audit-logs"
              />
            </div>
          </div>

          {/* System Health */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SystemHealthCard />
          </div>
        </>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
